import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { useAuthStore } from "../../auth/store";
import { AppDataGrid, gridExportMatrix, type AppColumn } from "../../components/AppDataGrid";
import { FormTitle } from "../../components/FormTitle";
import { fmtLocalDateTime, nowClientWire } from "../../utils/datetime";
import { useConfirm, showDialog } from "../../components/confirm";
import { useNotify } from "../../components/notifications";
import { TopbarStepper } from "../../components/StepIsland/TopbarStepper";
import { type IslandStep } from "../../components/StepIsland/StepIsland";
import { StepHint } from "../../components/StepHint/StepHint";
import { NotesListPopup, useNotesCount } from "./SdmsProjectNotes";
import { TrendIcon, TrendChartPopup, resolveRoutes, type TrendRoute, type TrendRow } from "../trend";
import { useHotkeys, formatCombo, type HotkeyDef } from "../../components/hotkeys";
// Stage 3 (Permit-To-Notification) — shared permit-limit parse + stacked/red cell.
import { parsePermitLimits, permitAlertText, permitResultValue, permitRowClassFor, type PermitLimit } from "./permitLimit";
import { usePermitRowColors } from "./permitRowColors";
import { renderPermitAlertCell, renderPermitLimitCell } from "./permitLimitCell";
import { permitLimitAutoFit, PERMIT_LIMIT_WIDTH } from "./permitLimitWidth";
import { RESULT_GRID_NO_ZEBRA } from "./resultGrids";
import { formatSdmsNumeric } from "./sdmsNumericFormat";
import "../backlog/backlog.css";
// Reuse the SDMS detail's tab styling (dv-tabs / dv-tab / dv-active) so the
// Sample Results / QC Results tabs look exactly like SDMS Result Validation.
import "./SdmsPackageDetail.css";
// Reuse the REE/RTT maximize styling (.ree-maximize-btn / .ree-detail-maximized)
// for the embedded grid's Maximize/Minimize (user request 2026-09-04).
import "./resultEntryQueryPanel.css";

/**
 * Result Validation / Approval DETAIL view — the drill-in from the manual Result
 * Validation OR Result Approval list (ALPACA's ResultEntry_Validation /
 * ResultEntry_Approval DashboardView).  ONE component serves BOTH Data-Review
 * stages via the batch's `mode` prop; the only differences are the endpoint base,
 * the editable stamp columns, and the terminal action verb:
 *   • mode 'validation' (default) — Level-2; ValidatedBy/ValidatedDate editable;
 *     action = Validate (Status 1→2 PendingApproval).
 *   • mode 'approval'             — Level-3; ApprovedBy/ApprovedDate editable while
 *     ValidatedBy/ValidatedDate are read-only (prior stage); action = Approve
 *     (Status 2→3 PendingReporting).
 *
 * Per-sample results for one QC batch / job, split into Sample Results / QC
 * Results tabs, with Project Notes · Rollback · Validate|Approve (reusing the
 * list-view endpoints).  The editable stamp columns mirror ALPACA's inline
 * Employee-lookup + date-picker cells; ticking a row previews the stamp and the
 * DB write happens on submit.  First two columns (+ checkbox) are FROZEN.  Built
 * on the shared formKit components per the BO UI Standard.
 */

interface SampleRow {
  id: string;
  jobID?: string | null; // (Result Approval only) injected from the batch — see load()
  jobOid?: string | null; // (multi-job embed only) the owning job's Oid — stamped in load() so
                          // doAction/doRollback can group ticked rows by job (task RTCFR 2026-09-21)
  qcBatchID: string | null;
  uqabid: string | null; // batch Oid — job mode collects these to Validate/Rollback
  sys: string | null;
  sampleId: string | null; // linked sample code (QC/Surrogate/ISTD tabs); null for non-sample QC
  sampleName: string | null;
  groupTest: string | null;
  test: string | null;
  method: string | null;
  parameter: string | null;
  numericResult: string | null;
  numericDecimals?: string | null; // the row's SDMS Decimal setting (display rounding only)
  result: string | null;
  units: string | null;
  rl: string | null;
  ucl: string | null;   // UCLLimit — Blank QC control limit; QC tab, Blank rows only
  mdl: string | null;
  loq: string | null;
  spikeAmount: string | null;
  spikeAmountUnit: string | null;
  rec: string | null;
  rpd: string | null;
  analyzedDate: string | null;
  analyzedBy: string | null;
  validatedDate: string | null;
  validatedBy: string | null;
  approvedDate: string | null; // approval mode only
  approvedBy: string | null; // approval mode only
  matrix: string | null;
  component: string | null;
  statusText: string | null;
  statusId: string | null;
  isQc: boolean;
  qcType: string | null;
  isSurrogate: boolean;         // Testparameter.Surroagate — Surrogate tab
  isInternalStandard: boolean;  // Testparameter.InternalStandard — Internal Standard tab
  statusCode: number;
  // Trend routing (task RTCFR, 2026-09-10): site (Sample-tab site trend) + Test+Matrix+
  // Method Oid (QC/surrogate/ISTD series scope) for the shared Trend popup.
  siteName?: string | null;
  testMethodOid?: string | null;
  // Stage 3 (Permit-To-Notification): the row's applicable permit limits as a raw
  // JSON array from the reader (null when the job has no permit), parsed once on load
  // into `permitLimits` for the stacked/red Permit Limit cell (permitLimit.ts).
  permitLimitsJson?: string | null;
  permitLimits?: PermitLimit[];
}

export interface ResultValidationBatch {
  /** Batch Oid (scope 'batch') OR job/Samplecheckin Oid (scope 'job'). */
  id: string;
  qcBatchID: string | null;
  client: string | null;
  projectID: string | null;
  /** Project name — shown in the header identity line. */
  projectName?: string | null;
  /** Job ID label — shown in the header identity line. */
  jobID?: string | null;
  /** Samplecheckin Oid for the row's JOB.  In QCBatchID mode the Sample tab is
   *  scoped to this job (QC stays batch-wide); passed to validate/rollback so
   *  only this job's samples flip.  Present in both modes. */
  jobOid?: string | null;
  /** 'batch' (QCBatchID mode, default) = ONE job's samples in one batch + that
   *  batch's QC (per-(batch,job)); 'job' (JobID mode) = the whole job across its
   *  batches — mirrors ALPACA's two "Search By" Retrieve targets. */
  scope?: "batch" | "job";
  /** 'validation' (default) = Level-2 Result Validation (Status 1→2, ValidatedBy/Date
   *  editable). 'approval' = Level-3 Result Approval (Status 2→3, ApprovedBy/Date
   *  editable, ValidatedBy/Date read-only). The SAME component serves both. */
  mode?: "validation" | "approval";
}

interface Props {
  /** The job/batch to review. May be null in EMBEDDED mode (Result Approval first
   *  page) → renders an EMPTY grid (headers only, no fetch) until a query resolves
   *  a job (user request 2026-09-04). Full-page callers always pass a batch. */
  batch: ResultValidationBatch | null;
  /** (embedded, task RTCFR 2026-09-21) The FULL set of eligible selected jobs when
   *  the user multi-selects JobIDs in the Result Approval / Validation Query panel.
   *  When this holds ≥2 jobs the embed loads EVERY job's samples (one single-job GET
   *  per job, merged + QC-deduped) and Approve/Rollback act across all of them (one
   *  per-job call each — the API/SP contract is single-job and unchanged). With 0–1
   *  job (or when absent) the component uses `batch` and behaves EXACTLY as before —
   *  so the full-page detail and Analysis Queue are untouched. `batch` is still the
   *  FIRST job (drives the header / notes / empty-state / remount key). */
  jobs?: ResultValidationBatch[];
  onClose: () => void;
  /** Called after a successful Validate / Rollback — parent refreshes the list and closes. */
  onDone: () => void;
  /** MAC write permission — when false Rollback / Validate / Approve are disabled
   *  and a read-only banner is shown. Defaults to true. */
  canWrite?: boolean;
  /** Render ONLY the review body (guidance · Notes · Rollback · Approve · tabs ·
   *  grid) with NO page chrome (no breadcrumb / stepper / FormTitle / Back), for
   *  embedding inline in the Result Approval first page (task RTCFR, 2026-09-04) —
   *  "the second-page grid + its logic on page one". Same data / columns /
   *  checkbox / approve logic as the full-page detail. */
  embedded?: boolean;
  /** (embedded) Which Data-Review stage this embed serves — 'approval' (Result
   *  Approval) or 'validation' (Result Validation, task RTCFR 2026-09-05). When
   *  embedded the batch is NULL by default (empty grid), so the stage can't be read
   *  from batch.mode; this prop drives the endpoints / stamp columns / action verb
   *  ("Approve" vs "Validate") even before a job resolves. Non-embedded full-page
   *  detail keeps reading batch.mode. Defaults to 'approval' (the original embed). */
  mode?: "validation" | "approval";
  /** (embedded) Publishes the active tab's export payload so the HOST's control
   *  bar can render the "Export to" button (user request 2026-09-04 moved it out
   *  of the embed toolbar). */
  onExportData?: (d: { filename: string; headers: string[]; rows: string[][] }) => void;
  /** (embedded) Publishes the review state so the HOST can render the
   *  "Review · Approve" instruction bar directly below the title (user request
   *  2026-09-04 moved it out of the embed body). `completed` is the brief
   *  post-submit flash and `selectedCount` the ticked-row count across both tabs —
   *  both gate the host's Rollback / Approve. */
  onReviewState?: (s: { reviewLeft: number; actionVerb: string; stampLabel: string; completed: boolean; selectedCount: number }) => void;
  /** (embedded) Imperative handle so the HOST control bar's Rollback / Approve
   *  buttons drive this grid's actions (moved out of the embed toolbar, user
   *  request 2026-09-04). */
  actionsRef?: React.MutableRefObject<{ rollback: () => void; approve: () => void } | null>;
  /** (embedded) Scope the loaded rows to these QC Batch IDs — set when the host's
   *  pending-selection QC Batch tab loads a batch (RTCFR 2026-09-28, the REE
   *  `batchIds` equivalent). The job(s) are fetched exactly as before; only rows of
   *  the chosen batch(es) are kept, so review / Validate / Approve / Rollback — which
   *  act on ticked rows — only ever see that batch. Omitted = unchanged behaviour. */
  batchIds?: string[];
}

// Endpoint base is mode-derived (see the component): manual Result Validation
// (Level-2) vs Result Approval (Level-3).  Both expose the same sub-routes
// (/{batchOid}/samples, /job/{jobOid}/samples, /{batchOid}/samples/save,
// /rollback, /rollback/precheck) plus their terminal action (/validate | /approve).
const ENDPOINT_VAL = "/api/v1/analysis/manual-result-validation";
const ENDPOINT_APPR = "/api/v1/analysis/result-approval";
// Employee roster for the ValidatedBy/ApprovedBy dropdown — ALPACA's Employee
// lookup: "N/A" first, then the full roster (incl. Administrator) sorted.
const EMP_OPTIONS_URL = "/api/v1/analysis/result-validation/validated-by-options";

// ALPACA parity: empty cells render BLANK, not a '—' placeholder.
function fmtDateTime(v?: string | null): string {
  return v ? fmtLocalDateTime(v) || "" : "";
}

// Column set mirrors ALPACA's ResultEntry_Validation grid.  All cells render
// BLANK when empty (ALPACA parity — no '—' placeholder).  The first two columns
// carry explicit numeric widths so the FROZEN-column offsets compute correctly.
// Leading JobID column — prepended ONLY in Result Approval mode (user request
// 2026-09-04), so Result Validation's grids are unchanged. The value is injected
// from the batch in load() (the samples SP returns no standalone JobID field).
const JOBID_COL: AppColumn<SampleRow> = {
  field: "jobID", header: "JobID", width: 130,
  text: (r) => r.jobID ?? "", render: (r) => r.jobID ?? "",
};

const COLS: AppColumn<SampleRow>[] = [
  { field: "sys", header: "SysSampleCode", width: 150, text: (r) => r.sys ?? "", render: (r) => r.sys ?? "" },
  {
    field: "sampleName",
    header: "SampleName",
    width: 180,
    text: (r) => r.sampleName ?? "",
    render: (r) => r.sampleName ?? "",
  },
  { field: "groupTest", header: "GroupTest", text: (r) => r.groupTest ?? "", render: (r) => r.groupTest ?? "" },
  { field: "test", header: "Test", text: (r) => r.test ?? "", render: (r) => r.test ?? "" },
  // Method (MethodName/MethodNumber) is HIDDEN in ALPACA's Result Validation AND
  // Approval Sample Results ListViews (Model.xafml Index="-1", verified 2026-07-10),
  // and LOQ is not a column there at all — so neither is shown on the Sample tab.
  { field: "parameter", header: "Parameter", text: (r) => r.parameter ?? "", render: (r) => r.parameter ?? "" },
  {
    field: "numericResult",
    header: "NumericResult",
    // Rounded to the row's SDMS Decimal setting with the SDMS trim rule (task RTCFR,
    // 2026-10-13; same function as Result Tracking). Display only — the stored value,
    // Result and the permit-limit checks are untouched; no setting -> shown as stored.
    text: (r) => formatSdmsNumeric(r.numericResult, r.numericDecimals, null),
    render: (r) => formatSdmsNumeric(r.numericResult, r.numericDecimals, null),
  },
  { field: "result", header: "Result", text: (r) => r.result ?? "", render: (r) => r.result ?? "" },
  { field: "units", header: "Units", text: (r) => r.units ?? "", render: (r) => r.units ?? "" },
  { field: "rl", header: "RL", text: (r) => r.rl ?? "", render: (r) => r.rl ?? "" },
  { field: "mdl", header: "MDL", text: (r) => r.mdl ?? "", render: (r) => r.mdl ?? "" },
  {
    field: "analyzedDate",
    header: "AnalyzedDate",
    text: (r) => fmtDateTime(r.analyzedDate),
    render: (r) => fmtDateTime(r.analyzedDate),
  },
  { field: "analyzedBy", header: "AnalyzedBy", text: (r) => r.analyzedBy ?? "", render: (r) => r.analyzedBy ?? "" },
  {
    field: "validatedDate",
    header: "ValidatedDate",
    text: (r) => fmtDateTime(r.validatedDate),
    render: (r) => fmtDateTime(r.validatedDate),
  },
  { field: "validatedBy", header: "ValidatedBy", text: (r) => r.validatedBy ?? "", render: (r) => r.validatedBy ?? "" },
  { field: "matrix", header: "Matrix", text: (r) => r.matrix ?? "", render: (r) => r.matrix ?? "" },
  // LIMS Sample Results order after ValidatedBy/ApprovedBy: Matrix · QCBatchID ·
  // Component · Status · StatusID (verified vs the ResultEntry_Approval grid,
  // 2026-07-10 — QCBatchID precedes Component).
  { field: "qcBatchID", header: "QCBatchID", text: (r) => r.qcBatchID ?? "", render: (r) => r.qcBatchID ?? "" },
  { field: "component", header: "Component", text: (r) => r.component ?? "", render: (r) => r.component ?? "" },
  { field: "statusText", header: "Status", type: "status", text: (r) => r.statusText ?? "" },
  { field: "statusId", header: "StatusID", text: (r) => r.statusId ?? "", render: (r) => r.statusId ?? "" },
  // Stage 3 (Permit-To-Notification, ADDITIVE — appended so the LIMS-verified order
  // above stays byte-identical): every applicable permit limit stacked, red when the
  // result is outside it (permitLimit.ts). Blank when the job carries no permit. `text`
  // keeps the scalar for export/filter; ALPACA compares/flags, never determines compliance.
  {
    field: "permitLimit",
    header: "PermitLimit",
    width: PERMIT_LIMIT_WIDTH.min,
    // Width follows the content (shared rule, RTCFR "Dynamic Permit Limit Column Width").
    autoFit: permitLimitAutoFit<SampleRow>((r) => r.permitLimits, () => ""),
    text: (r) => (r.permitLimits && r.permitLimits.length ? r.permitLimits.map((l) => l.limit1 ?? "").join(" · ") : ""),
    render: (r) => renderPermitLimitCell(r.permitLimits, permitResultValue(r.result, r.numericResult), r.units, ""),
  },
  // Alert (RTCFR "Display 'Notify lab' in Alert Column", 2026-10-13) — next to PermitLimit,
  // as on REE / RT: "⚑ Notify lab" when an applicable permit limit is violated, "—" when
  // none is, blank without a permit or a result. Shared permitAlertState / cell.
  {
    field: "alert",
    header: "Alert",
    width: 110,
    text: (r) => permitAlertText(r.permitLimits, r.result, r.numericResult, r.units, ""),
    render: (r) => renderPermitAlertCell(r.permitLimits, r.result, r.numericResult, r.units, ""),
  },
];

// QC Results tab columns.  LIMS orders this grid DIFFERENTLY per Data-Review stage
// (both verified in Model.xafml, 2026-07-10):
//   • Result Validation (SampleParameter_ListView_Copy_QCResultValidation) — this
//     base array's order: … SpikeAmountUnit · %Rec · %RPD · AnalyzedDate · AnalyzedBy
//     · ValidatedDate · ValidatedBy · Status · Matrix · Method (Method LAST).
//   • Result Approval  (SampleParameter_ListView_Copy_QCResultApproval) — Method
//     right after Test, and AnalyzedDate BETWEEN %Rec and %RPD:
//     … Test · Method · Parameter … SpikeAmountUnit · %Rec · AnalyzedDate · %RPD ·
//     AnalyzedBy · ValidatedDate · ValidatedBy · [ApprovedDate · ApprovedBy] · Status
//     · Matrix.
// QC_COLS holds the VALIDATION order; qcColsForMode() reorders those two columns for
// Approval.  Status renders the enum text on BOTH (the QC ListViews bind Status to the
// Samplestatus ENUM, not SampleStatusNew — the samples SP returns enum text for QC
// rows).  The stamp columns are inline-editable per mode (see withStampEditors).
// First two columns carry explicit widths for the frozen-column offsets.
const QC_COLS: AppColumn<SampleRow>[] = [
  {
    field: "qcBatchID",
    header: "QCBatchID",
    width: 150,
    text: (r) => r.qcBatchID ?? "",
    render: (r) => r.qcBatchID ?? "",
  },
  { field: "qcType", header: "QCType", width: 150, text: (r) => r.qcType ?? "", render: (r) => r.qcType ?? "" },
  { field: "sys", header: "SysSampleCode", text: (r) => r.sys ?? "", render: (r) => r.sys ?? "" },
  // Linked parent sample (task RTCFR, 2026-09-10) — QC/Surrogate/ISTD tabs (surr/istd
  // inherit via the qcCols filter); blank for non-sample QC. Relationship-sourced.
  { field: "sampleId", header: "SampleID", text: (r) => r.sampleId ?? "", render: (r) => r.sampleId ?? "" },
  { field: "test", header: "Test", text: (r) => r.test ?? "", render: (r) => r.test ?? "" },
  { field: "parameter", header: "Parameter", text: (r) => r.parameter ?? "", render: (r) => r.parameter ?? "" },
  {
    field: "numericResult",
    header: "NumericResult",
    // Rounded to the row's SDMS Decimal setting with the SDMS trim rule (task RTCFR,
    // 2026-10-13; same function as Result Tracking). Display only — the stored value,
    // Result and the permit-limit checks are untouched; no setting -> shown as stored.
    text: (r) => formatSdmsNumeric(r.numericResult, r.numericDecimals, null),
    render: (r) => formatSdmsNumeric(r.numericResult, r.numericDecimals, null),
  },
  { field: "result", header: "Result", text: (r) => r.result ?? "", render: (r) => r.result ?? "" },
  { field: "units", header: "Units", text: (r) => r.units ?? "", render: (r) => r.units ?? "" },
  { field: "rl", header: "RL", text: (r) => r.rl ?? "", render: (r) => r.rl ?? "" },
  // UCLLimit — Blank QC control limit, immediately after RL (RTCFR Phase 2, 2026-09-15).
  { field: "ucl", header: "UCLLimit", text: (r) => r.ucl ?? "", render: (r) => r.ucl ?? "" },
  { field: "mdl", header: "MDL", text: (r) => r.mdl ?? "", render: (r) => r.mdl ?? "" },
  // SpikeAmount is numeric → RIGHT-aligned, matching ALPACA's QC grid (user request 2026-07-10).
  {
    field: "spikeAmount",
    header: "SpikeAmount",
    align: "right",
    text: (r) => r.spikeAmount ?? "",
    render: (r) => r.spikeAmount ?? "",
  },
  {
    field: "spikeAmountUnit",
    header: "SpikeAmountUnit",
    text: (r) => r.spikeAmountUnit ?? "",
    render: (r) => r.spikeAmountUnit ?? "",
  },
  { field: "rec", header: "%Rec", text: (r) => r.rec ?? "", render: (r) => r.rec ?? "" },
  { field: "rpd", header: "%RPD", text: (r) => r.rpd ?? "", render: (r) => r.rpd ?? "" },
  {
    field: "analyzedDate",
    header: "AnalyzedDate",
    text: (r) => fmtDateTime(r.analyzedDate),
    render: (r) => fmtDateTime(r.analyzedDate),
  },
  { field: "analyzedBy", header: "AnalyzedBy", text: (r) => r.analyzedBy ?? "", render: (r) => r.analyzedBy ?? "" },
  {
    field: "validatedDate",
    header: "ValidatedDate",
    text: (r) => fmtDateTime(r.validatedDate),
    render: (r) => fmtDateTime(r.validatedDate),
  },
  { field: "validatedBy", header: "ValidatedBy", text: (r) => r.validatedBy ?? "", render: (r) => r.validatedBy ?? "" },
  { field: "statusText", header: "Status", type: "status", text: (r) => r.statusText ?? "" },
  { field: "matrix", header: "Matrix", text: (r) => r.matrix ?? "", render: (r) => r.matrix ?? "" },
  { field: "method", header: "Method", text: (r) => r.method ?? "", render: (r) => r.method ?? "" },
];

// Fields dropped on the Surrogate / Internal Standard tabs (user request 2026-09-09).
// This QC set carries only RL · MDL · %Rec · %RPD among the target columns. Surrogate
// keeps %Rec (recovery); Internal Standard drops it too. (LOQ/UQL/D.F./Ctrl-Limit
// columns are not part of this grid, so they need no removal here.)
const SURR_DROP = new Set(["rl", "ucl", "mdl", "rpd"]);
const ISTD_DROP = new Set(["rl", "ucl", "mdl", "rpd", "rec"]);

// QC column order per Data-Review stage.  Validation uses QC_COLS as-is; Approval
// reorders two columns to match SampleParameter_ListView_Copy_QCResultApproval:
// Method moves to right after Test, and AnalyzedDate moves BETWEEN %Rec and %RPD.
// Reuses the SAME column objects (by field) — no duplicated render/text logic.
function qcColsForMode(mode: "validation" | "approval"): AppColumn<SampleRow>[] {
  if (mode !== "approval") return QC_COLS;
  const by = (f: keyof SampleRow) => QC_COLS.find((c) => c.field === f)!;
  return [
    by("qcBatchID"),
    by("qcType"),
    by("sys"),
    by("test"),
    by("method"),
    by("parameter"),
    by("numericResult"),
    by("result"),
    by("units"),
    by("rl"),
    by("ucl"),
    by("mdl"),
    by("spikeAmount"),
    by("spikeAmountUnit"),
    by("rec"),
    by("analyzedDate"),
    by("rpd"),
    by("analyzedBy"),
    by("validatedDate"),
    by("validatedBy"),
    by("statusText"),
    by("matrix"),
  ];
}

// The two editable stamp columns (a 24-hour DATE+TIME editor + an Employee combo),
// mirroring ALPACA's inline ValidatedDate/ValidatedBy (Validation) and
// ApprovedDate/ApprovedBy (Approval) cells.  In VALIDATION mode ValidatedDate/By
// are editable.  In APPROVAL mode the ApprovedDate/By pair is INSERTED after
// ValidatedBy and is editable, while ValidatedDate/By become the prior-stage,
// read-only stamp.  ALPACA parity: DataReviewController's isVerifyView stamps
// ApprovedDate/ApprovedBy, else ValidatedDate/ValidatedBy.
const APPROVED_DATE_COL: AppColumn<SampleRow> = {
  field: "approvedDate",
  header: "ApprovedDate",
  text: (r) => fmtDateTime(r.approvedDate),
  render: (r) => fmtDateTime(r.approvedDate),
};
const APPROVED_BY_COL: AppColumn<SampleRow> = {
  field: "approvedBy",
  header: "ApprovedBy",
  text: (r) => r.approvedBy ?? "",
  render: (r) => r.approvedBy ?? "",
};

function withStampEditors(
  cols: AppColumn<SampleRow>[],
  empOptions: string[],
  mode: "validation" | "approval",
): AppColumn<SampleRow>[] {
  const combo = { type: "combo" as const, options: empOptions };
  const dt = { type: "datetime" as const };
  if (mode === "approval") {
    const out: AppColumn<SampleRow>[] = [];
    for (const c of cols) {
      out.push(c); // ValidatedDate / ValidatedBy stay read-only (prior stage).
      if (c.field === "validatedBy") {
        out.push({ ...APPROVED_DATE_COL, edit: dt });
        out.push({ ...APPROVED_BY_COL, edit: combo });
      }
    }
    return out;
  }
  return cols.map((c) => {
    if (c.field === "validatedBy") return { ...c, edit: combo };
    if (c.field === "validatedDate") return { ...c, edit: dt };
    return c;
  });
}

// Map an RV/RA grid row → the shared Trend router's TrendRow (task RTCFR, 2026-09-10).
// Reuses the SAME routing/popup as SDMS Result Validation + REE + RT — no trend logic
// is duplicated here. Sample → site trend (siteName); sample-linked QC → parent sample
// (sampleId); Surrogate / Internal Standard → their series (flags + testMethodOid).
/** Permit-limit row tint — the lab's rules from Settings ▸ Permit Row Colors (permitRowClassFor),
 *  the same rule as Result Entry Express and the SDMS grid. Used by both grids below. */
function permitRowClassOf(r: SampleRow): string | undefined {
  return permitRowClassFor(r.permitLimits, r.result, r.numericResult, r.units, r.mdl);
}

function rvToTrendRow(r: SampleRow): TrendRow {
  return {
    id:                 r.id,
    sys:                r.sys || null,
    parameter:          r.parameter || null,
    siteName:           r.siteName || null,
    qcTypeName:         r.qcType || null,
    parentSys:          r.sampleId || null,
    testMethodOid:      r.testMethodOid || null,
    isInternalStandard: r.isInternalStandard ?? false,
    isSurrogate:        r.isSurrogate ?? false,
    analyzedDate:        r.analyzedDate ?? null,
  };
}

export default function ResultValidationDetail({ batch, jobs, onClose, onDone, canWrite = true, embedded = false, mode: embeddedMode = "approval", onExportData, onReviewState, actionsRef, batchIds }: Props) {
  usePermitRowColors();   // Settings ▸ Permit Row Colors — re-colour the rows when the lab's rules load
  const [rows, setRows] = useState<SampleRow[]>([]);
  // QC-batch row scope (see Props.batchIds) — a stable string key so load() only
  // re-runs when the SET changes, not on every new array identity from the host.
  const batchScopeKey = embedded && batchIds && batchIds.length ? batchIds.join("|") : "";
  const inBatchScope = useCallback((r: { qcBatchID: string | null }) => {
    if (!batchScopeKey) return true;
    return !!r.qcBatchID && batchScopeKey.split("|").includes(r.qcBatchID);
  }, [batchScopeKey]);
  // Shared Trend wiring (task RTCFR, 2026-09-10) — same router + popup as SDMS/REE/RT.
  const trendRouteMap = useMemo(() => resolveRoutes(rows.map(rvToTrendRow)), [rows]);
  const [openTrend, setOpenTrend] = useState<
    { route: TrendRoute; label: string; batchOid: string; method: string | null } | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [tab, setTab] = useState<"sample" | "qc" | "surrogate" | "istd">("sample");
  const [notesOpen, setNotesOpen] = useState(false);
  // (embedded) Maximize/Restore the grid panel (RT parity, user request 2026-09-04)
  // — fullscreens the embed as a fixed overlay; Esc or the Minimize icon restores.
  const [maximized, setMaximized] = useState(false);
  // True for the brief "✓ Approved"/"✓ Validated" flash between a successful
  // submit and the auto-return to the list (user request 2026-07-20) — without
  // this the action step's stepper state never had a moment to render 'done'
  // before the component unmounted.
  const [completed, setCompleted] = useState(false);
  // Employee roster for the ValidatedBy dropdown.
  const [empOptions, setEmpOptions] = useState<string[]>([]);
  // Per-row checkbox selection (ALPACA parity — the grid's first column).  Persists
  // ACROSS tabs so the guidance strip's "(N left)" review count spans Sample + QC
  // results.  Validate/Rollback still act on the whole batch.
  const [selRows, setSelRows] = useState<Set<string | number>>(new Set());
  // Rows whose ValidatedDate/ValidatedBy we PREVIEW-stamped on tick (so un-tick can
  // restore them, and a pre-validated row is never clobbered).  Cleared on reload.
  const [previewIds, setPreviewIds] = useState<Set<string>>(new Set());
  const notify = useNotify();
  const { confirm, prompt } = useConfirm();
  const notesCount = useNotesCount(batch?.id ?? "", batch?.jobOid ?? null);
  // Current user — the validator whose name previews in ValidatedBy on tick
  // (ALPACA client stamp: s.cpusername).  The DB write on Validate is authoritative.
  const meName = useAuthStore((s) => s.user?.name || s.user?.email || s.user?.id || "");

  // JobID mode loads the whole job's results (every QC batch) from the job
  // endpoint; QCBatchID mode loads one batch.  Mirrors ALPACA's two Retrieve
  // targets (DataReviewQueryPanelController.dataReviewNextAction).
  const isJob = batch?.scope === "job";

  // ── Multi-job embed (task RTCFR 2026-09-21) — the user multi-selected JobIDs in
  // the RA/RV Query panel.  With ≥2 eligible jobs the embed loads EVERY job and acts
  // per-job (see load / doActionMulti / doRollbackMulti).  With 0–1 job the single
  // `batch` path runs UNCHANGED, so the full-page detail + Analysis Queue are untouched.
  const multiJobs = useMemo(() => (embedded ? (jobs ?? []).filter((j) => !!j?.id) : []), [embedded, jobs]);
  const isMulti = multiJobs.length >= 2;

  // ── Validation vs Approval (the ONE difference between the two Data-Review
  // stages) — endpoints, the editable stamp columns, action verb, audit form and
  // status wording all key off this.  Everything else is identical.  EMBEDDED mode
  // reads the explicit `mode` prop (the batch is NULL by default, so batch.mode
  // can't be trusted before a job resolves); full-page detail reads batch.mode.
  // (task RTCFR 2026-09-05: Result Validation now embeds too, mode='validation'.) ─
  const isAppr = embedded ? embeddedMode === "approval" : batch?.mode === "approval";
  // The leading JobID column + per-row JobID stamping apply to any JOB-scoped grid:
  // both the embedded Data-Review grids (RA + RV) and full-page Result Approval.
  const showJobId = isAppr || embedded;
  const ENDPOINT = isAppr ? ENDPOINT_APPR : ENDPOINT_VAL;
  const stampByKey = (isAppr ? "approvedBy" : "validatedBy") as "approvedBy" | "validatedBy";
  const stampDateKey = (isAppr ? "approvedDate" : "validatedDate") as "approvedDate" | "validatedDate";
  // LIMS date-order guard on the editable stamp-date cell (DataReviewQueryPanel
  // BatchEditEndEditing): the new stamp must be >= its FLOOR date (AnalyzedDate for
  // Validation, ValidatedDate for Approval) and <= the current time — else the
  // matching LIMS message and the edit is rejected.
  const floorDateKey = (isAppr ? "validatedDate" : "analyzedDate") as "validatedDate" | "analyzedDate";
  const dateFloorMsg = isAppr
    ? "The approved date must be greater than the validated date."
    : "The validated date must be greater than the analyzed date.";
  const dateCurrentMsg = isAppr
    ? "The approved date must be less than the current date."
    : "The validated date must be less than the current date.";
  const actionVerb = isAppr ? "Approve" : "Validate"; // button + confirm
  const nextStage = isAppr ? "Pending Reporting" : "Pending Approval";
  const formName = isAppr ? "Result Approval" : "Result Validation";
  const pageId = isAppr ? "rappr" : "rval";
  const stampLabel = isAppr ? "ApprovedBy / Date" : "ValidatedBy / Date";

  const load = useCallback(async () => {
    // ── MULTI-JOB (task RTCFR 2026-09-21): the user selected ≥2 JobIDs. Load EVERY
    // eligible job with the SAME single-job endpoint (contract unchanged) and MERGE.
    // Each row is stamped with its owning job's JobID + Oid; QC controls shared by
    // two jobs' batches are deduped by SampleParameter Oid (they advance once). ──
    if (isMulti) {
      setLoading(true);
      const t0 = performance.now();
      try {
        const results = await Promise.all(
          multiJobs.map(async (jb) => {
            const url = jb.scope === "job"
              ? `${ENDPOINT}/job/${jb.id}/samples`
              : `${ENDPOINT}/${jb.id}/samples${jb.jobOid ? `?jobOid=${jb.jobOid}` : ""}`;
            const r = await api.get(url);
            return { jb, data: (r.data as { data: SampleRow[] }).data ?? [] };
          }),
        );
        const merged: SampleRow[] = [];
        const seen = new Set<string>();
        for (const { jb, data } of results) {
          for (const row of data) {
            if (!inBatchScope(row)) continue; // QC Batch tab load → that batch's rows only
            if (seen.has(row.id)) continue; // dedup QC shared across jobs' batches
            seen.add(row.id);
            merged.push({ ...row, jobID: jb.jobID ?? null, jobOid: jb.jobOid ?? jb.id, permitLimits: parsePermitLimits(row.permitLimitsJson) });
          }
        }
        setRows(merged);
        setSelRows(new Set());
        setPreviewIds(new Set());
      } catch {
        setRows([]);
        notify.error("Failed to load job results — check the API logs.");
      } finally {
        setLoadMs(performance.now() - t0);
        setLoading(false);
      }
      return;
    }
    // EMBEDDED with no job resolved yet (Result Approval default) → EMPTY grid, no
    // fetch (user request 2026-09-04).
    if (!batch) { setRows([]); setSelRows(new Set()); setPreviewIds(new Set()); setLoadMs(null); setLoading(false); return; }
    setLoading(true);
    const t0 = performance.now();
    try {
      // Job scope = the whole job (all its batches).  Batch scope with a jobOid =
      // ONE job's samples in this batch + the batch's QC (per-(batch,job)); without
      // a jobOid = the whole batch.
      const url = isJob
        ? `${ENDPOINT}/job/${batch.id}/samples`
        : `${ENDPOINT}/${batch.id}/samples${batch.jobOid ? `?jobOid=${batch.jobOid}` : ""}`;
      const r = await api.get(url);
      const data = (r.data as { data: SampleRow[] }).data ?? [];
      // Result Approval shows a leading JobID column; the samples SP returns no
      // standalone JobID field, so stamp the batch's JobID onto each row (the view
      // is job-scoped — every row belongs to this job). Validation mode is untouched.
      // Stage 3: parse each row's permit limits once here (undefined = no permit).
      setRows(data.filter(inBatchScope).map((row) => ({
        ...row,
        ...(showJobId ? { jobID: batch.jobID ?? null } : {}),
        permitLimits: parsePermitLimits(row.permitLimitsJson),
      })));
      // Fresh server state → drop any tick preview + selection.
      setSelRows(new Set());
      setPreviewIds(new Set());
    } catch {
      setRows([]);
      notify.error(`Failed to load ${isJob ? "job" : "batch"} results — check the API logs.`);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [isMulti, multiJobs, ENDPOINT, batch?.id, isJob, showJobId, batch?.jobID, notify, inBatchScope]);

  useEffect(() => {
    void load();
  }, [load]);

  // Employee roster for the ValidatedBy dropdown — loaded once.
  useEffect(() => {
    let alive = true;
    api
      .get(EMP_OPTIONS_URL, { silentLoading: true } as never)
      .then((r) => {
        if (alive) setEmpOptions((r.data as { data: string[] }).data ?? []);
      })
      .catch(() => {
        /* dropdown just falls back to the current value */
      });
    return () => {
      alive = false;
    };
  }, []);

  // Browser Back closes the detail (parity with the SDMS package overlay).
  // Skipped when embedded inline (Result Approval first page) — there is no
  // navigation to pop back from.
  useEffect(() => {
    if (embedded) return;
    window.history.pushState({ rvDetail: true }, "");
    const onPop = () => onClose();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [onClose, embedded]);

  // Four result tabs (task RTCFR, 2026-09-08), MUTUALLY EXCLUSIVE so no row appears in
  // more than one tab. Priority: a Surrogate parameter → Surrogate tab; an Internal
  // Standard parameter → Internal Standard tab; otherwise the isQc split (QC / Sample).
  // Surrogate/ISTD are Test-setup parameter flags and can sit on real samples OR QC
  // controls, so they're pulled OUT of the Sample/QC tabs. (Never both flags at once.)
  const surrogateRows = useMemo(() => rows.filter((r) => r.isSurrogate), [rows]);
  const istdRows      = useMemo(() => rows.filter((r) => r.isInternalStandard && !r.isSurrogate), [rows]);
  const sampleRows    = useMemo(() => rows.filter((r) => !r.isQc && !r.isSurrogate && !r.isInternalStandard), [rows]);
  // QC tab row order — LIMS parity (SampleParameter_ListView_Copy_QCResultApproval /
  // Result Approval + Validation): group by QCType ASCENDING, then SysSampleCode
  // DESCENDING within each type (e.g. CCV Low2 above CCV Low1, Method Blank2 above
  // Method Blank1). The backend SP returns SysSampleCode order (which scatters the
  // QC types); this refines the DISPLAY order for the QC tab only — Sample rows keep
  // the SP order. (2026-08-11, client "match LIMS row order".)
  const qcRows = useMemo(
    () =>
      rows
        .filter((r) => r.isQc && !r.isSurrogate && !r.isInternalStandard)
        .sort((a, b) => {
          const byType = (a.qcType ?? "").localeCompare(b.qcType ?? "");
          if (byType !== 0) return byType;
          return (b.sys ?? "").localeCompare(a.sys ?? ""); // SysSampleCode DESC within type
        }),
    [rows],
  );
  const activeRows = tab === "sample" ? sampleRows
    : tab === "surrogate" ? surrogateRows
    : tab === "istd" ? istdRows
    : qcRows;
  // Results still un-ticked across BOTH tabs — drives the prototype's "(N left)"
  // guidance and the "Review complete" state.
  const reviewLeft = rows.reduce((n, r) => n + (selRows.has(r.id) ? 0 : 1), 0);
  // Per-tab completion for the stepper below — an empty tab (e.g. "QC Sample
  // Results (0)") counts as done with nothing to review, same as a tab whose
  // every row is ticked. Per StepIsland's own contract ("an OPTIONAL step
  // becomes 'done' only when its action actually happens, never on tab-open"),
  // this must be tick-based, not "has this tab merely been opened" — the prior
  // version marked `qc` 'todo' forever whenever it had 0 rows (nothing to ever
  // tick), which looked stuck/broken even after a successful Approve.
  const sampleDone = sampleRows.length === 0 || sampleRows.every((r) => selRows.has(r.id));
  const qcDone = qcRows.length === 0 || qcRows.every((r) => selRows.has(r.id));

  // Validate / Rollback act on QC-batch Oids.  Batch mode = this one batch; job
  // mode = every distinct batch the job's loaded rows belong to (the existing
  // endpoints already accept many batchIds).
  const targetBatchIds = useMemo<string[]>(
    () => (!batch ? [] : isJob ? Array.from(new Set(rows.map((r) => r.uqabid).filter((x): x is string => !!x))) : [batch.id]),
    [isJob, rows, batch],
  );

  const mode: "validation" | "approval" = isAppr ? "approval" : "validation";
  // Trend column (task RTCFR, 2026-09-10) — shared <TrendIcon>/<TrendChartPopup> + route
  // resolver (no trend logic duplicated here). RV/RA carry no RawData column, so Trend is
  // inserted right after Units (the result block), consistently on every tab. Enabled only
  // when the row has an analytical batch (uqabid) to scope the series — same as SDMS/REE/RT.
  const trendCol: AppColumn<SampleRow> = {
    field: "trend", header: "Trend", width: 70, sortable: false, filterable: false,
    text: () => "",
    render: (r) => (
      <TrendIcon
        route={r.uqabid ? trendRouteMap.get(r.id) : undefined}
        onOpen={() => {
          const route = trendRouteMap.get(r.id);
          if (route?.routable && r.uqabid) {
            setOpenTrend({
              route,
              label: `${r.sys ?? ""}${r.parameter ? ` · ${r.parameter}` : ""}`,
              batchOid: r.uqabid,
              method: r.method ?? null,
            });
          }
        }}
      />
    ),
  };
  const withTrend = (cols: AppColumn<SampleRow>[]): AppColumn<SampleRow>[] => {
    const i = cols.findIndex((c) => c.field === "units");
    return i >= 0 ? [...cols.slice(0, i + 1), trendCol, ...cols.slice(i + 1)] : [...cols, trendCol];
  };
  // Result Approval prepends a leading JobID column on BOTH tabs (user request
  // 2026-09-04); Result Validation keeps its exact ALPACA column set.
  const sampleCols = useMemo(() => {
    const base = withStampEditors(COLS, empOptions, mode);
    if (!showJobId) return withTrend(base);
    // Job-scoped grids (RA + embedded RV): JobID first, then QCBatchID (moved up
    // next to JobID, user request 2026-09-04), then the rest.
    const qc = base.find((c) => c.field === "qcBatchID");
    const rest = base.filter((c) => c.field !== "qcBatchID");
    return withTrend(qc ? [JOBID_COL, qc, ...rest] : [JOBID_COL, ...base]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empOptions, mode, showJobId, trendRouteMap]);
  const qcCols = useMemo(() => {
    const base = withStampEditors(qcColsForMode(mode), empOptions, mode);
    // QC_COLS already starts with QCBatchID, so JobID + base = JobID, QCBatchID, …
    return withTrend(showJobId ? [JOBID_COL, ...base] : base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empOptions, mode, showJobId, trendRouteMap]);
  // Surrogate / Internal Standard trim the QC columns (user request 2026-09-09).
  // This QC set only carries RL · MDL · %Rec · %RPD among the target columns (no
  // D.F./LOQ/UQL/Ctrl-Limit columns exist here), so: Surrogate drops RL · MDL · %RPD
  // (keeps %Rec — surrogate recovery); Internal Standard drops %Rec too.
  const surrogateCols = useMemo(() => qcCols.filter(c => !SURR_DROP.has(c.field as string)), [qcCols]);
  const istdCols      = useMemo(() => qcCols.filter(c => !ISTD_DROP.has(c.field as string)), [qcCols]);

  // Inline-edit commit — ValidatedBy (Employee display name) / ValidatedDate
  // (wire string).  PENDING-ONLY (user request 2026-07-10, ALPACA batch-edit
  // parity): the edit updates the local row buffer but is NOT persisted here and
  // shows NO "Saved successfully." toast.  Like LIMS's grid batch buffer, the
  // changes are committed to the DB only when the reviewer clicks Validate/Approve
  // (see doAction → /samples/save then /validate|/approve).  Back / Refresh discards.
  const onCellCommit = useCallback(
    (row: SampleRow, col: AppColumn<SampleRow>, value: string) => {
      const v = value === "" ? null : value;
      // LIMS BatchEditEndEditing date-order validation on the editable stamp-date
      // cell (ApprovedDate in approval mode, ValidatedDate in validation).  Runs
      // ONLY when BOTH the new stamp and its floor date exist (LIMS gates on both
      // non-null).  On failure show the LIMS message and REJECT the edit — the
      // inline editor has already closed, so the cell reverts to its stored value.
      if (col.field === stampDateKey && v) {
        const floor = row[floorDateKey];
        const t = new Date(v).getTime();
        if (!Number.isNaN(t)) {
          if (floor && t < new Date(floor).getTime()) {
            notify.error(dateFloorMsg);
            return;
          }
          if (t > Date.now()) {
            notify.error(dateCurrentMsg);
            return;
          }
        }
      }
      setRows((prev) => prev.map((r) => (r.id === row.id ? ({ ...r, [col.field]: v } as SampleRow) : r)));
      // A manual edit on the stamp employee/date cell means the reviewer has
      // deliberately taken ownership of this row's stamp — it's no longer just
      // the tick-to-preview auto-fill from onSelectionChange (browser-clock
      // based), so doAction must now send it to /samples/save even though the
      // untouched auto-preview must NOT be (see doAction — sending the
      // untouched preview persisted the VIEWER's browser clock instead of the
      // DB server's, a few minutes off — 2026-07-20).
      if (col.field === stampByKey || col.field === stampDateKey) {
        setPreviewIds((prev) => {
          if (!prev.has(row.id)) return prev;
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        });
      }
    },
    [stampDateKey, stampByKey, floorDateKey, dateFloorMsg, dateCurrentMsg, notify],
  );

  // Tick → auto-fill ValidatedDate / ValidatedBy (ALPACA DataReviewController's
  // client-side SelectionChanged: SetCellValue(ValidatedDate, today) +
  // SetCellValue(ValidatedBy, currentUser) on select, cleared on de-select).  This
  // is a PREVIEW only — like LIMS, the authoritative DB stamp happens on Validate
  // (AnalysisWriter stamps ValidatedBy=@UserOid, ValidatedDate=now WHERE
  // ValidatedBy IS NULL).  A row already validated by someone else is preserved
  // (only rows WE preview-stamped are cleared on un-tick).  Works on BOTH tabs —
  // the handler stamps by row id, and Sample/QC rows share `rows`.
  const onSelectionChange = useCallback(
    (next: Set<string | number>) => {
      const stamp = nowClientWire();
      const nextPreview = new Set(previewIds);
      setRows((prev) =>
        prev.map((r) => {
          const isSel = next.has(r.id);
          const wasSel = selRows.has(r.id);
          if (isSel === wasSel) return r;
          if (isSel) {
            // Stamp the stage's preview (Approved* in approval mode, Validated* in
            // validation mode) ONLY if that row isn't already stamped (preserve existing).
            if (!r[stampByKey] && !r[stampDateKey]) {
              nextPreview.add(r.id);
              return { ...r, [stampDateKey]: stamp, [stampByKey]: meName } as SampleRow;
            }
            return r;
          }
          // De-selected: clear only the cells WE preview-stamped.
          if (nextPreview.has(r.id)) {
            nextPreview.delete(r.id);
            return { ...r, [stampDateKey]: null, [stampByKey]: null } as SampleRow;
          }
          return r;
        }),
      );
      setPreviewIds(nextPreview);
      setSelRows(next);
    },
    [selRows, previewIds, meName, stampByKey, stampDateKey],
  );

  // Scope label for confirms / messages.  A jobOid means the action is job-scoped
  // (JOBID mode, or QCBatchID per-job) → "job 260623002"; only a bare whole-batch
  // action reads "QC batch QB…_076".
  const scopeLabel = isJob || batch?.jobOid ? `job ${batch?.jobID ?? ""}` : `QC batch ${batch?.qcBatchID ?? ""}`;

  // ── MULTI-JOB Approve/Validate (task RTCFR 2026-09-21) — the SAME guards as the
  // single path, then ONE per-job call (server jobOid branch, API/SP contract
  // unchanged). Ticked rows are grouped by their stamped jobOid; a QC control shared
  // by two jobs' batches advances under its first job and is idempotently a no-op for
  // the rest (already past Status 2). Counts are aggregated across jobs. ──
  const doActionMulti = useCallback(async () => {
    const tickedSampleBatches = new Set<string>(
      sampleRows.filter((r) => selRows.has(r.id)).map((r) => r.uqabid).filter((x): x is string => !!x),
    );
    const missingQc = qcRows.some((qc) => !!qc.uqabid && tickedSampleBatches.has(qc.uqabid) && !selRows.has(qc.id));
    if (missingQc) { notify.error("Select the QC results from the QC result tab."); return; }
    if (selRows.size === 0) { notify.warning("Please select an item to proceed further."); return; }

    const ok = await confirm({
      title: `${actionVerb} Results`,
      message: `${actionVerb} ${multiJobs.length} selected jobs? Their pending results move to ${nextStage}.`,
      confirmLabel: actionVerb,
    });
    if (!ok) return;

    // Group the ticked rows by their owning job (stamped in load()).
    const byJob = new Map<string, string[]>();
    for (const r of rows) {
      if (!selRows.has(r.id)) continue;
      const oid = r.jobOid ?? null;
      if (!oid) continue;
      byJob.set(oid, [...(byJob.get(oid) ?? []), r.id]);
    }
    try {
      let total = 0;
      let firstSkip: string | null = null;
      for (const jb of multiJobs) {
        const oid = jb.jobOid ?? jb.id;
        const ids = byJob.get(oid);
        if (!ids || ids.length === 0) continue;
        // Commit this job's pending stamp edits (mirrors the single path).
        const edits = rows
          .filter((r) => ids.includes(r.id) && !previewIds.has(r.id) && (r[stampDateKey] || r[stampByKey]))
          .map((r) => (isAppr
            ? { id: r.id, approvedBy: r.approvedBy, approvedDate: r.approvedDate }
            : { id: r.id, validatedBy: r.validatedBy, validatedDate: r.validatedDate }));
        if (edits.length > 0) {
          try { await api.post(`${ENDPOINT}/${jb.id}/samples/save`, { edits }, { silentLoading: true } as never); }
          catch { /* non-fatal — the flip stamps server-now for un-saved rows */ }
        }
        const res = await api.post(`${ENDPOINT}/${isAppr ? "approve" : "validate"}`, {
          batchIds: [],
          jobOid: oid,
          sampleParameterIds: ids,
        });
        const d = res.data as { count: number; skipped?: Record<string, string> };
        total += d.count ?? 0;
        if (!firstSkip && (d.count ?? 0) === 0) firstSkip = Object.values(d.skipped ?? {})[0] ?? null;
      }
      if (total > 0) {
        setCompleted(true);
        await showDialog({
          type: "success",
          title: isAppr ? "Approved" : "Validated",
          message: `${isAppr ? "Approved" : "Validated"} successfully.`,
        });
        onDone();
      } else {
        notify.error(firstSkip ?? `Nothing to ${actionVerb.toLowerCase()} in the selected jobs.`);
      }
    } catch {
      notify.error(`${actionVerb} failed — check the API logs.`);
    }
  }, [rows, sampleRows, qcRows, selRows, previewIds, multiJobs, ENDPOINT, isAppr, stampByKey, stampDateKey, actionVerb, nextStage, confirm, notify, onDone]);

  // ── MULTI-JOB Rollback (task RTCFR 2026-09-21) — precheck each job, block on the
  // first blocked job, prompt for ONE reason, then roll back per job. ──
  const doRollbackMulti = useCallback(async () => {
    if (selRows.size === 0) { notify.warning("Please select an item to proceed further."); return; }
    const byJob = new Map<string, string[]>();
    for (const r of rows) {
      if (!selRows.has(r.id)) continue;
      const oid = r.jobOid ?? null;
      if (!oid) continue;
      byJob.set(oid, [...(byJob.get(oid) ?? []), r.id]);
    }
    for (const jb of multiJobs) {
      const oid = jb.jobOid ?? jb.id;
      const ids = byJob.get(oid);
      if (!ids || ids.length === 0) continue;
      try {
        const pre = await api.post(`${ENDPOINT}/rollback/precheck`, { batchIds: [], jobOid: oid });
        const blocked = (pre.data as { blocked?: Record<string, string> }).blocked ?? {};
        if (Object.keys(blocked).length > 0) { notify.error(Object.values(blocked)[0] ?? "Rollback failed."); return; }
      } catch { /* precheck hiccup shouldn't block; the write re-checks */ }
    }
    const reason = await prompt({
      title: "Result Rollback",
      message: "Enter a reason for the rollback.",
      placeholder: "Rollback reason…",
      required: true,
      confirmLabel: "Rollback",
    });
    if (reason === null) return;
    try {
      let total = 0;
      let firstSkip: string | null = null;
      for (const jb of multiJobs) {
        const oid = jb.jobOid ?? jb.id;
        const ids = byJob.get(oid);
        if (!ids || ids.length === 0) continue;
        const res = await api.post(`${ENDPOINT}/rollback`, {
          batchIds: [],
          jobOid: oid,
          reason,
          sampleParameterIds: ids,
        });
        const d = res.data as { count: number; skipped?: Record<string, string> };
        total += d.count ?? 0;
        if (!firstSkip && (d.count ?? 0) === 0) firstSkip = Object.values(d.skipped ?? {})[0] ?? null;
      }
      if (total > 0) {
        await showDialog({ type: "success", title: "Rollback", message: "Rollbacked successfully." });
        onDone();
      } else {
        notify.error(firstSkip ?? "Nothing to roll back in the selected jobs.");
      }
    } catch {
      notify.error("Rollback failed — check the API logs.");
    }
  }, [rows, selRows, multiJobs, ENDPOINT, prompt, notify, onDone]);

  const doAction = useCallback(async () => {
    if (isMulti) { await doActionMulti(); return; }
    if (!batch) return;
    // ── Selection guards — ALPACA ResultValidation_Execute / ResultApproval_Execute
    // ── (ResultEntryViewController) via the shared §13 notifications.  Order matches
    // LIMS: the QC guard fires before the "nothing selected" info.
    //   • A ticked SAMPLE whose QC batch has an UN-ticked QC row → red error
    //     "Select the QC results from the QC result tab." (tick samples ⇒ must tick
    //     their QC too).  No symmetric sample guard (LIMS is asymmetric).
    //   • Nothing ticked in either tab → centered WARNING modal "Please select an
    //     item…" (§13: blocking guards are modals, not banners — 2026-08-11).
    const tickedSampleBatches = new Set<string>(
      sampleRows
        .filter((r) => selRows.has(r.id))
        .map((r) => r.uqabid)
        .filter((x): x is string => !!x),
    );
    const missingQc = qcRows.some((qc) => !!qc.uqabid && tickedSampleBatches.has(qc.uqabid) && !selRows.has(qc.id));
    if (missingQc) {
      notify.error("Select the QC results from the QC result tab.");
      return;
    }
    if (selRows.size === 0) {
      notify.warning("Please select an item to proceed further.");
      return;
    }

    // Result Validation AND Approval are JOB-scoped on the server (a non-batched job's
    // samples have no QC batch, so targetBatchIds can be empty) — proceed as long as we
    // have the job.
    if (targetBatchIds.length === 0 && !batch.jobOid) {
      notify.error(`Nothing pending to ${actionVerb.toLowerCase()}.`);
      return;
    }
    const ok = await confirm({
      title: `${actionVerb} Results`,
      message: `${actionVerb} ${scopeLabel}? Its pending results move to ${nextStage}.`,
      confirmLabel: actionVerb,
    });
    if (!ok) return;
    try {
      // Commit the pending stamp edits (Approved* / Validated*) for the ticked rows
      // (LIMS commits the grid batch buffer on submit — the edits were held locally,
      // NOT saved on each keystroke).  Silent — no per-edit toast.  The flip then
      // PRESERVES these (ISNULL) and stamps any still-null cells with the current
      // user + server-now.
      //
      // previewIds EXCLUDED here (fixed 2026-07-20): a ticked-but-never-manually-
      // edited row is still just onSelectionChange's tick-to-preview auto-fill —
      // a BROWSER-clock value (nowClientWire), not the DB server's. Sending it
      // here would persist that browser-clock stamp unconditionally, and the
      // flip's ISNULL(...,@StampedAt) below would then find the cell already
      // non-null and never reach its own (now DB-server-sourced) stamp. Leaving
      // an untouched preview row's stamp NULL here lets the flip fill it in
      // correctly instead. Only rows the reviewer actually edited via
      // onCellCommit (no longer in previewIds) are sent.
      const edits = rows
        .filter((r) => selRows.has(r.id) && !previewIds.has(r.id) && (r[stampDateKey] || r[stampByKey]))
        .map((r) =>
          isAppr
            ? { id: r.id, approvedBy: r.approvedBy, approvedDate: r.approvedDate }
            : { id: r.id, validatedBy: r.validatedBy, validatedDate: r.validatedDate },
        );
      if (edits.length > 0) {
        try {
          await api.post(`${ENDPOINT}/${batch.id}/samples/save`, { edits }, { silentLoading: true } as never);
        } catch {
          /* non-fatal — the flip still stamps server-now for un-saved rows */
        }
      }
      // jobOid scopes the flip to THIS job's samples (+ the batch's QC), so acting on
      // a per-(batch,job) view doesn't touch the batch's other jobs.  sampleParameterIds
      // = the ticked rows (samples + their QC) → PARTIAL: the server advances ONLY those
      // SampleParameter.Oids, matching ALPACA ResultEntryViewController (SelectedObjects
      // only), instead of every pending row in the job/batch.
      const res = await api.post(`${ENDPOINT}/${isAppr ? "approve" : "validate"}`, {
        batchIds: targetBatchIds,
        jobOid: batch.jobOid ?? null,
        sampleParameterIds: Array.from(selRows),
      });
      const d = res.data as { count: number; skipped?: Record<string, string> };
      if (d.count > 0) {
        // Flash the stepper's action step as done (✓ Approved/Validated), then the
        // MILESTONE success is the shared CENTERED modal (§18.6); on OK, return to
        // the list (replaces the old flash + auto-return top banner).
        setCompleted(true);
        await showDialog({
          type: "success",
          title: isAppr ? "Approved" : "Validated",
          message: `${isAppr ? "Approved" : "Validated"} successfully.`,
        });
        onDone();
      } else
        notify.error(
          Object.values(d.skipped ?? {})[0] ??
            `Nothing to ${actionVerb.toLowerCase()} in this ${isJob ? "job" : "batch"}.`,
        );
    } catch {
      notify.error(`${actionVerb} failed — check the API logs.`);
    }
  }, [
    rows,
    sampleRows,
    qcRows,
    selRows,
    previewIds,
    targetBatchIds,
    batch,
    scopeLabel,
    isJob,
    isAppr,
    ENDPOINT,
    actionVerb,
    nextStage,
    stampByKey,
    stampDateKey,
    confirm,
    notify,
    onDone,
    isMulti,
    doActionMulti,
  ]);

  const doRollback = useCallback(async () => {
    if (isMulti) { await doRollbackMulti(); return; }
    if (!batch) return;
    // Selection guard — ALPACA Rollback_Execute (ResultEntryViewController line
    // 2680-2683): nothing ticked in EITHER tab → centered WARNING modal "Please
    // select an item to proceed further." (the same `selectchkbox` message as
    // Validate; §13 blocking guard → modal, 2026-08-11). Rollback is symmetric —
    // no QC-specific guard, just "at least one item selected".
    if (selRows.size === 0) {
      notify.warning("Please select an item to proceed further.");
      return;
    }
    if (targetBatchIds.length === 0 && !(isAppr && batch.jobOid)) {
      notify.error("Nothing pending to roll back.");
      return;
    }
    try {
      const pre = await api.post(`${ENDPOINT}/rollback/precheck`, {
        batchIds: targetBatchIds,
        jobOid: batch.jobOid ?? null,
      });
      const blocked = (pre.data as { blocked?: Record<string, string> }).blocked ?? {};
      if (Object.keys(blocked).length > 0) {
        notify.error(Object.values(blocked)[0] ?? "Rollback failed.");
        return;
      }
    } catch {
      /* precheck hiccup shouldn't block; the write re-checks */
    }
    const reason = await prompt({
      title: "Result Rollback",
      message: "Enter a reason for the rollback.",
      placeholder: "Rollback reason…",
      required: true,
      confirmLabel: "Rollback",
    });
    if (reason === null) return;
    try {
      const res = await api.post(`${ENDPOINT}/rollback`, {
        batchIds: targetBatchIds,
        jobOid: batch.jobOid ?? null,
        reason,
        sampleParameterIds: Array.from(selRows),
      });
      const d = res.data as { count: number; skipped?: Record<string, string> };
      if (d.count > 0) {
        await showDialog({ type: "success", title: "Rollback", message: "Rollbacked successfully." });
        onDone();
      } else
        notify.error(Object.values(d.skipped ?? {})[0] ?? `Nothing to roll back in this ${isJob ? "job" : "batch"}.`);
    } catch {
      notify.error("Rollback failed — check the API logs.");
    }
  }, [selRows, targetBatchIds, batch, isJob, isAppr, ENDPOINT, prompt, notify, onDone, isMulti, doRollbackMulti]);

  // Shared grid card (used by both the full-page and embedded layouts).
  const activeCols = tab === "sample" ? sampleCols
    : tab === "surrogate" ? surrogateCols
    : tab === "istd" ? istdCols
    : qcCols;
  const gridCard = (
    <div className="bl-card bl-card-flex" style={{ borderTopLeftRadius: 0 }}>
      <AppDataGrid<SampleRow>
        className={RESULT_GRID_NO_ZEBRA}
        rows={activeRows}
        columns={activeCols}
        getRowId={(r) => r.id}
        loading={loading}
        loadMs={loadMs}
        /* Export lives on the host control bar and Maximize is this form's own
           .ree-maximize-btn — suppress the grid's own header cluster so it
           doesn't show twice (user report 2026-09-23). */
        hideHeaderTools
        selectable
        selectedIds={selRows}
        onSelectionChange={onSelectionChange}
        stickyColumns={2}
        getRowClassName={permitRowClassOf}
        onCellCommit={onCellCommit}
        emptyText={!batch
          ? "Set the Query criteria and click Load to view the matching Sample / QC results."
          : tab === "sample" ? "No sample results in this batch."
          : tab === "surrogate" ? "No surrogate parameters in this batch."
          : tab === "istd" ? "No internal standard parameters in this batch."
          : "No QC results in this batch."}
      />
    </div>
  );

  // (embedded) Publish the active tab's export payload so the host control bar
  // renders "Export to" (moved out of the embed toolbar, user request 2026-09-04).
  const embedExport = useMemo(
    () => ({ filename: `result-approval-${batch?.jobID ?? batch?.qcBatchID ?? batch?.id ?? 'empty'}-${tab}`,
      ...gridExportMatrix(activeRows, activeCols) }),
    [batch?.jobID, batch?.qcBatchID, batch?.id, tab, activeRows, activeCols],
  );
  useEffect(() => { if (embedded) onExportData?.(embedExport); }, [embedded, onExportData, embedExport]);
  // (embedded) Publish the review state so the host renders the "Review · Approve"
  // bar below the title (moved out of the embed body, user request 2026-09-04).
  useEffect(() => {
    if (embedded) onReviewState?.({ reviewLeft, actionVerb, stampLabel, completed, selectedCount: selRows.size });
  }, [embedded, onReviewState, reviewLeft, actionVerb, stampLabel, completed, selRows]);
  // (embedded) Expose Rollback / Approve so the host control bar's buttons drive
  // them (moved out of the embed toolbar, user request 2026-09-04).
  useEffect(() => {
    if (embedded && actionsRef) actionsRef.current = { rollback: () => void doRollback(), approve: () => void doAction() };
  }, [embedded, actionsRef, doRollback, doAction]);
  // Esc restores from the maximized (full-page) grid panel — via the shared
  // hotkey layer (standing rule 14), never a raw keydown listener.  Registered
  // AFTER rvKeys below so, while maximized, this Esc wins over the "Back" esc.

  // ── Module action keys (CLAUDE.md standing rule 14, §18.6, ADR-0013) — each def
  //    MIRRORS a full-page header/toolbar button's exact handler + disabled gate,
  //    grouped under the form title (Validate / Approve by mode).  EMBEDDED mode
  //    owns no primary buttons here — the host list page drives Rollback / Approve
  //    via actionsRef and there is no Back / Refresh — so the array is EMPTY there.
  //    The call itself is UNCONDITIONAL and sits before the first early return, so
  //    hook order never changes.
  const rvKeys: HotkeyDef[] = embedded ? [
    // EMBEDDED owns no primary buttons (host drives Validate/Approve via actionsRef),
    // but its own Project Notes button IS here, so bind that one (mirrors its gate).
    { combo: "alt+j", label: "Project Notes", group: formName, order: 6,
      run: () => setNotesOpen(true), disabled: !batch },
  ] : [
    { combo: isAppr ? "alt+a" : "alt+v", label: actionVerb, group: formName, order: 1,
      run: () => void doAction(), disabled: completed || !canWrite },
    { combo: "alt+b", label: "Rollback", group: formName, order: 2,
      run: () => void doRollback(), disabled: completed || !canWrite },
    { combo: "alt+r", label: "Refresh", group: formName, order: 3,
      run: () => void load() },
    { combo: "esc", label: "Back", group: formName, order: 4,
      run: () => onClose(), allowInInput: false },
    { combo: "alt+j", label: "Project Notes", group: formName, order: 6,
      run: () => setNotesOpen(true) },
  ];
  useHotkeys(rvKeys);
  useHotkeys(maximized
    ? { combo: 'esc', label: 'Exit full screen', group: formName, order: 5, run: () => setMaximized(false) }
    : null);

  // EMBEDDED (Result Approval first page): the SAME data / columns / checkbox /
  // approve logic as the full-page detail, minus the page chrome (breadcrumb,
  // stepper, FormTitle, Back). The segregation-of-duties banner is intentionally
  // NOT shown here (removed from the first page on 2026-09-04); the server still
  // enforces analyst≠approver.
  if (embedded) {
    return (
      // Maximized → fixed full-page overlay (RT parity, `.ree-detail-maximized`);
      // otherwise flex to fill the first-page grid slot.
      <div className={maximized ? "ree-detail-maximized" : undefined}
        style={maximized ? { padding: "10px 14px" } : { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        {!canWrite && (
          <div className="rval-readonly-banner">
            <i className="ti ti-eye" /> <b>Read-only access</b> — you can view results but cannot Rollback or Approve. Contact your administrator to request Write permission.
          </div>
        )}
        {/* ONE compact row: Sample Results | QC Results | Project Notes (left) ·
            [Maximize] (right). Rollback / Approve live in the host control bar; when
            MAXIMIZED that bar is hidden behind the overlay, so they ride beside the
            Minimize button here (user request 2026-09-04). Review guidance + Refresh
            + Export to are in the host chrome. */}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 6, flexWrap: "wrap" }}>
          <div className="dv-tabs" style={{ marginBottom: 0 }}>
            <div className={`dv-tab ${tab === "sample" ? "dv-active" : ""}`} onClick={() => setTab("sample")}>
              Sample Results
            </div>
            <div className={`dv-tab ${tab === "qc" ? "dv-active" : ""}`} onClick={() => setTab("qc")}>
              QC Results
            </div>
            <div className={`dv-tab ${tab === "surrogate" ? "dv-active" : ""}`} onClick={() => setTab("surrogate")}>
              Surrogate
            </div>
            <div className={`dv-tab ${tab === "istd" ? "dv-active" : ""}`} onClick={() => setTab("istd")}>
              Internal Standard
            </div>
          </div>
          <button className="ca-btn" style={{ marginBottom: 4 }} onClick={() => setNotesOpen(true)}
            disabled={!batch} title={`Project notes for this batch (${formatCombo("alt+j")})`}>
            <i className="ti ti-notes" style={{ color: "var(--ai-icon-audit)" }} /> Project Notes ({notesCount})
          </button>
          <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 4, marginRight: 14 }}>
            {/* When maximized the top control bar (with Rollback / Approve) is hidden
                behind the overlay, so surface them here beside Minimize. */}
            {maximized && canWrite && (
              <>
                <button className="ca-btn" onClick={() => void doRollback()} disabled={completed || selRows.size === 0}
                  title={selRows.size === 0 ? "Tick one or more Sample / QC results to roll back." : isAppr ? "Roll this job back to Pending Validation" : "Roll this batch back to Pending Entry"}>
                  <i className="ti ti-arrow-back-up" style={{ color: "var(--ai-red)" }} /> Rollback
                </button>
                <button className="ca-btn ca-btn-solid" onClick={() => void doAction()} disabled={completed || selRows.size === 0}
                  title={selRows.size === 0 ? `Tick one or more Sample / QC results to ${actionVerb.toLowerCase()}.` : `${actionVerb} this ${isJob || batch?.jobOid ? "job" : "batch"}'s pending results`}>
                  <i className="ti ti-circle-check" /> {completed ? `${actionVerb}d` : actionVerb}
                </button>
              </>
            )}
            <button type="button" className="ree-maximize-btn"
              onClick={() => setMaximized(m => !m)}
              title={maximized ? "Restore grid to inline view (Esc)." : "Expand grid to full page."}
              aria-label={maximized ? "Restore grid" : "Maximize grid"}>
              <i className={`ti ${maximized ? "ti-arrows-minimize" : "ti-arrows-maximize"}`} aria-hidden="true" />
            </button>
          </span>
        </div>
        {gridCard}
        {notesOpen && batch && (
          <NotesListPopup
            batchId={batch.id}
            businessObject={formName}
            batchTest={formName}
            jobOid={batch.jobOid ?? null}
            batchLabel={batch.jobID ?? batch.qcBatchID ?? ""}
            onClose={() => setNotesOpen(false)}
            onChanged={() => {}}
          />
        )}
        {openTrend && (
          <TrendChartPopup
            route={openTrend.route}
            batchOid={openTrend.batchOid}
            sampleLabel={openTrend.label}
            method={openTrend.method}
            permitLimit={null}
            permitWarn={null}
            onClose={() => setOpenTrend(null)}
          />
        )}
      </div>
    );
  }

  // Full-page detail (non-embedded) always has a batch (rvDetail); narrow it here so
  // the rest of the render can use batch.* directly.
  if (!batch) return null;

  return (
    <div className="bl-page">
      {/* Result Validation workflow stepper (BO UI Standard §5) — docked in the
          top bar. Select Job is done (a batch was loaded); Sample Results / QC
          Sample Results go 'done' once every row in that tab is ticked (or the
          tab has 0 rows to review) — COMPLETION wins even while you're still
          viewing that tab (fixed 2026-07-20, user request: match the SDMS
          package detail's own stepper, which marks a step done as soon as it's
          reviewed, not just when you've tabbed away from it). Only an
          UNFINISHED tab you're currently on shows 'active'; tab-open alone
          never counts as done on its own (StepIsland's own contract). The
          final action step goes 'active' once nothing is left to tick
          (matching the "Review complete" banner below) and flips to 'done' for
          a brief moment after a successful submit (see `completed` /
          doAction) — a fixed 900ms window before doAction's
          setTimeout(onDone) unmounts this view. */}
      <TopbarStepper
        steps={
          [
            { id: "select", label: "Select Job", state: "done" },
            {
              id: "sample",
              label: "Sample Results",
              state: sampleDone ? "done" : tab === "sample" ? "active" : "todo",
            },
            { id: "qc", label: "QC Sample Results", state: qcDone ? "done" : tab === "qc" ? "active" : "todo" },
            { id: "action", label: actionVerb, state: completed ? "done" : reviewLeft === 0 ? "active" : "todo" },
          ] as IslandStep[]
        }
      />
      <div className="bl-top">
        {/* Breadcrumb navigation (user request 2026-07-10 — the detail was missing it;
            the LIST already shows Home › Data Review › Result Validation, and LIMS's
            detail carries a breadcrumb too).  "Result Validation" links back to the
            list; the trailing crumb is the job/batch being reviewed. */}
        <div className="dp-breadcrumb">
          <span>Home</span>
          <i className="ti ti-chevron-right" />
          <span>Data Review</span>
          <i className="ti ti-chevron-right" />
          <span
            className="dp-bc-link"
            role="button"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onClose();
            }}
            title={`Back to the ${formName} list`}
          >
            {formName}
          </span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">{batch.jobID ?? batch.qcBatchID ?? formName}</span>
        </div>
        <div className="dp-title-row">
          <div className="dp-titlewrap">
            <FormTitle
              pageId={pageId}
              title={formName}
              loadMs={loadMs}
              loading={loading}
              auditFormName={formName}
              auditSourceId={batch.id}
            />
          </div>
          {/* TOP-RIGHT actions (prototype parity): Back · Rollback · Validate/Approve
              — the terminal action is the orange solid primary. */}
          <div className="dp-title-actions">
            <button className="ca-btn" onClick={onClose} title={`Back to the ${formName} list (${formatCombo("esc")})`}>
              <i className="ti ti-arrow-left" /> Back
            </button>
            {/* Hidden without Write — the read-only banner above says why. */}
            {canWrite && (
              <button
                className="ca-btn"
                onClick={() => void doRollback()}
                disabled={completed}
                title={
                  (isAppr
                    ? "Roll this job back to Pending Validation"
                    : "Roll this batch back to Pending Entry") + ` (${formatCombo("alt+b")})`
                }
              >
                <i className="ti ti-arrow-back-up" style={{ color: "var(--ai-red)" }} /> Rollback
              </button>
            )}
            {canWrite && (
              <button
                className="ca-btn ca-btn-solid"
                onClick={() => void doAction()}
                disabled={completed}
                title={
                  `${actionVerb} this ${isJob || batch.jobOid ? "job" : "batch"}'s pending results`
                  + ` (${formatCombo(isAppr ? "alt+a" : "alt+v")})`
                }
              >
                <i className="ti ti-circle-check" /> {completed ? `${actionVerb}d` : actionVerb}
              </button>
            )}
          </div>
        </div>
        {/* Identity line under the title (user request 2026-07-09; refer prototype):
            "{QCBatchID | JobID} · Client · ProjectName" — the batch/job being reviewed. */}
        {[batch.qcBatchID ?? batch.jobID, batch.client, batch.projectName].some(Boolean) && (
          <div className="dp-subtitle">
            {/* QCBatchID · JobID · Client · ProjectName — per-(batch,job) shows both
                the batch and the job being reviewed; JobID mode has no QCBatchID. */}
            {[batch.qcBatchID, batch.jobID, batch.client, batch.projectName].filter(Boolean).join(" · ")}
          </div>
        )}
      </div>

      <div className="bl-grid-area">
        {/* ── Read-only MAC banner ──────────────────────────────────────── */}
        {!canWrite && (
          <div className="rval-readonly-banner">
            <i className="ti ti-eye" /> <b>Read-only access</b> — you can view results but cannot Rollback, Validate or Approve. Contact your administrator to request Write permission.
          </div>
        )}
        {/* Guidance strip (BO UI Standard §5, shared <StepHint>) — verbatim from the
            Data-Review prototype (user request 2026-07-09): the tick-to-review guidance
            with a live "(N left)" count, flipping to "Review complete" (green) when every
            Sample & QC result is ticked. */}
        {reviewLeft > 0 ? (
          <StepHint tone="guidance">
            <b>Review · {actionVerb}.</b> Review each result and <b>tick it</b> — ticking stamps <b>{stampLabel}</b>.
            Tick <b>every Sample Result and QC Sample Result</b> ({reviewLeft} left), then <b>{actionVerb}</b>. Each
            grid's header checkbox selects all.
          </StepHint>
        ) : (
          <StepHint tone="complete">
            <b>Review complete.</b> Every Sample &amp; QC result is ticked — click <b>{actionVerb}</b> to advance the
            job, or <b>Rollback</b>.
          </StepHint>
        )}
        {/* Result Approval — segregation-of-duties reminder (ALPACA
            ResultApproval_Execute: the approver must differ from the analyst).  Uses
            the SAME orange `guidance` tone as the review strip above / the Result
            Validation form (user request 2026-07-10: match Result Validation's colour). */}
        {isAppr && (
          // WORDING (2026-08-25): ANALYSIS, not "validation" — the gate compares the ANALYST
          // against the approver; a validator may approve what they validated. See the twin
          // banner in AnalysisListPage for the full note. The error DIALOG keeps ALPACA's
          // legacy wording for parity; these banners are ours alone.
          <StepHint tone="guidance">
            <i className="ti ti-info-circle" /> Analysis and approval of analysis results must be performed by{" "}
            <b>different users</b>.
          </StepHint>
        )}
        {/* Inline toolbar — Project Notes · Refresh. (Back · Rollback · Validate
            moved to the TOP-RIGHT header, prototype parity 2026-07-09; Save/Cancel
            changes removed — ValidatedBy/ValidatedDate edits auto-save on commit.) */}
        <div style={{ margin: "0 0 8px", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <button className="ca-btn" onClick={() => setNotesOpen(true)} title={`Project notes for this batch (${formatCombo("alt+j")})`}>
            <i className="ti ti-notes" style={{ color: "var(--ai-icon-audit)" }} /> Project Notes ({notesCount})
          </button>
          <button className="ca-btn" onClick={() => void load()} title={`Refresh (${formatCombo("alt+r")})`}>
            <i className="ti ti-refresh" /> Refresh
          </button>
        </div>

        {/* Sample Results / QC Results tabs — same tab bar as SDMS Result
            Validation (dv-tabs / dv-tab / dv-active). */}
        <div className="dv-tabs">
          <div className={`dv-tab ${tab === "sample" ? "dv-active" : ""}`} onClick={() => setTab("sample")}>
            Sample Results
          </div>
          <div className={`dv-tab ${tab === "qc" ? "dv-active" : ""}`} onClick={() => setTab("qc")}>
            QC Results
          </div>
          <div className={`dv-tab ${tab === "surrogate" ? "dv-active" : ""}`} onClick={() => setTab("surrogate")}>
            Surrogate
          </div>
          <div className={`dv-tab ${tab === "istd" ? "dv-active" : ""}`} onClick={() => setTab("istd")}>
            Internal Standard
          </div>
        </div>

        <div className="bl-card bl-card-flex" style={{ borderTopLeftRadius: 0 }}>
          <AppDataGrid<SampleRow>
            className={RESULT_GRID_NO_ZEBRA}
            rows={activeRows}
            columns={activeCols}
            getRowId={(r) => r.id}
            loading={loading}
            loadMs={loadMs}
            /* Own .ree-maximize-btn + host Export — no duplicate grid cluster
               (user report 2026-09-23). */
            hideHeaderTools
            selectable
            selectedIds={selRows}
            onSelectionChange={onSelectionChange}
            stickyColumns={2}
            getRowClassName={permitRowClassOf}
            onCellCommit={onCellCommit}
            emptyText={tab === "sample" ? "No sample results in this batch." : "No QC results in this batch."}
          />
        </div>
      </div>

      {notesOpen && (
        <NotesListPopup
          batchId={batch.id}
          // Business Object + Name Source of a NEW note = this form's caption ("Result
          // Approval" / "Result Validation") — ALPACA sets NoteSource = NameSource =
          // View.Caption; the API tags the saved note the same via `source`.
          businessObject={formName}
          batchTest={formName}
          // Manual Result Validation scopes notes to the ONE displayed job (ALPACA
          // "Result Validation") — representative job in QCBatchID mode, selected job
          // in JobID mode — and titles the popup with the JobID (LIMS "Project Notes
          // - 260623002"), not the QC batch.
          jobOid={batch.jobOid ?? null}
          batchLabel={batch.jobID ?? batch.qcBatchID ?? ""}
          onClose={() => setNotesOpen(false)}
          onChanged={() => {
            /* count auto-refreshes via useNotesCount */
          }}
        />
      )}
      {openTrend && (
        <TrendChartPopup
          route={openTrend.route}
          batchOid={openTrend.batchOid}
          sampleLabel={openTrend.label}
          method={openTrend.method}
          permitLimit={null}
          permitWarn={null}
          onClose={() => setOpenTrend(null)}
        />
      )}
    </div>
  );
}
